import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Flex,
    Input,
    InputGroup,
    InputLeftElement,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Spinner,
    Text,
    VStack,
    useToast,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { TbBuilding } from 'react-icons/tb';
import type { ElementType } from 'react';
import LocationSelector from '../../../components/LocationSelector';
import { useInventoryReportingChanges } from '../../../hooks/reporting/useInventoryReportingChanges';
import {
    getInventoryReportingExportErrorMessage,
    useExportInventoryReporting,
} from '../../../hooks/reporting/useExportInventoryReporting';
import { useReportingColumnPrefs } from '../../../hooks/useReportingColumnPrefs';
import type {
    InventoryReportingExportColumnKey,
    InventoryReportingExportFormat,
} from '../../../types/reporting';
import { columnDrawerRowsToPrefs, type SettingsColumnDrawerRow } from './SettingsColumnsPanel';
import AuditingInventoryTable from './AuditingInventoryTable';
import {
    AUDIT_INVENTORY_COLUMN_KEYS,
    AUDIT_INVENTORY_DEFAULT_HIDDEN,
    AUDIT_INVENTORY_EXPORT_COLUMN_KEYS,
    AUDIT_INVENTORY_LABELS,
    AUDIT_INVENTORY_PINNED_KEYS,
    AUDIT_INVENTORY_SETTINGS_LABELS,
} from '../auditingInventoryConfig';
import {
    useReportingInventoryFilters,
    type ReportingInventoryFilterSnapshot,
    type ReportingInventoryFiltersModel,
} from './useReportingInventoryFilters';

const PAGE_SIZE = 50;

export type AuditingTableSource = 'procurement' | 'inventory';

const TABLE_SOURCE_STORAGE_KEY = 'reporting_audit_table_source';

export function readAuditingTableSource(inventoryAvailable: boolean): AuditingTableSource {
    if (!inventoryAvailable) return 'procurement';
    try {
        const raw = localStorage.getItem(TABLE_SOURCE_STORAGE_KEY);
        if (raw === 'inventory' || raw === 'procurement') return raw;
    } catch {
        /* ignore */
    }
    return 'procurement';
}

export function writeAuditingTableSource(source: AuditingTableSource) {
    try {
        localStorage.setItem(TABLE_SOURCE_STORAGE_KEY, source);
    } catch {
        /* ignore */
    }
}

function SearchableCheckboxList({
    search,
    onSearchChange,
    searchPlaceholder,
    isLoading,
    options,
    selectedIds,
    onToggle,
}: {
    search: string;
    onSearchChange: (v: string) => void;
    searchPlaceholder: string;
    isLoading: boolean;
    options: Array<{ value: string; label: string }>;
    selectedIds: string[];
    onToggle: (value: string, label: string) => void;
}) {
    return (
        <VStack align="stretch" spacing={2}>
            <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.400" boxSize={3} />
                </InputLeftElement>
                <Input
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder={searchPlaceholder}
                />
            </InputGroup>
            {isLoading ? (
                <Flex justify="center" py={3}>
                    <Spinner size="sm" />
                </Flex>
            ) : (
                <VStack align="stretch" spacing={1} maxH="220px" overflowY="auto">
                    {options.map((opt) => (
                        <Checkbox
                            key={opt.value}
                            size="sm"
                            isChecked={selectedIds.includes(opt.value)}
                            onChange={() => onToggle(opt.value, opt.label)}
                        >
                            <Text fontSize="sm">{opt.label}</Text>
                        </Checkbox>
                    ))}
                    {options.length === 0 && (
                        <Text fontSize="sm" color="gray.500" px={1}>
                            No results
                        </Text>
                    )}
                </VStack>
            )}
        </VStack>
    );
}

function MultiSelectTrigger({
    label,
    selectedCount,
    children,
}: {
    label: string;
    selectedCount: number;
    children: ReactNode;
}) {
    return (
        <Popover placement="bottom-start" isLazy>
            <PopoverTrigger>
                <Button
                    size="sm"
                    variant="outline"
                    justifyContent="space-between"
                    fontWeight="normal"
                    w="100%"
                    rightIcon={
                        selectedCount > 0 ? (
                            <Text as="span" fontSize="xs" color="gray.500">
                                {selectedCount}
                            </Text>
                        ) : undefined
                    }
                >
                    {selectedCount > 0 ? `${label} (${selectedCount})` : label}
                </Button>
            </PopoverTrigger>
            <PopoverContent w="280px" maxW="90vw" zIndex={1500}>
                <PopoverBody p={3}>{children}</PopoverBody>
            </PopoverContent>
        </Popover>
    );
}

export function useAuditingInventory({
    startDate,
    endDate,
    locationId,
    preset,
    isActive,
    /** When provided (Inventory tab), reuse that shared filter-hook instance. */
    sharedFilters,
}: {
    startDate: string;
    endDate: string;
    locationId?: string;
    preset?: string;
    isActive: boolean;
    sharedFilters?: ReportingInventoryFiltersModel;
}) {
    const toast = useToast();
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(1);

    // Always call the hook (Rules of Hooks). When InventoryTab passes sharedFilters,
    // this owned instance stays idle (isActive false, empty search).
    const ownedFilters = useReportingInventoryFilters({
        locationId,
        isActive: isActive && !sharedFilters,
        searchQuery: sharedFilters ? '' : searchQuery,
    });
    const filters = sharedFilters ?? ownedFilters;

    const {
        selectedLocations,
        setSelectedLocations,
        selectedPartTypes,
        setSelectedPartTypes,
        selectedTagIds,
        selectedBinIds,
        selectedSupplierIds,
        tagSearch,
        setTagSearch,
        binSearch,
        setBinSearch,
        supplierSearch,
        setSupplierSearch,
        isTagsLoading,
        isBinsLoading,
        isSuppliersLoading,
        partTypeOptions,
        tagOptions,
        binOptions,
        supplierOptions,
        toggleId,
        setSelectedTagIds,
        setSelectedBinIds,
        setSelectedSupplierIds,
        setTagLabelById,
        setBinLabelById,
        setSupplierLabelById,
        apiFilters,
        locationIdsForApi,
        activeFilterCount,
        getSnapshot,
        restoreSnapshot,
    } = filters;

    useEffect(() => {
        setPage(1);
    }, [
        startDate,
        endDate,
        locationId,
        preset,
        apiFilters.search,
        selectedLocations,
        selectedPartTypes,
        selectedTagIds,
        selectedBinIds,
        selectedSupplierIds,
    ]);

    const offset = (page - 1) * PAGE_SIZE;
    const { data, isLoading, isFetching } = useInventoryReportingChanges(startDate, endDate, {
        locationIds: locationIdsForApi,
        preset,
        tagIds: selectedTagIds.length ? selectedTagIds : undefined,
        binIds: selectedBinIds.length ? selectedBinIds : undefined,
        partTypes: selectedPartTypes.length ? selectedPartTypes : undefined,
        supplierIds: selectedSupplierIds.length ? selectedSupplierIds : undefined,
        search: apiFilters.search,
        limit: PAGE_SIZE,
        offset,
        enabled: isActive,
    });

    const exportMutation = useExportInventoryReporting({
        onError: (error) => {
            toast({
                title: 'Export failed',
                description: getInventoryReportingExportErrorMessage(error),
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        },
    });

    const columnPrefs = useReportingColumnPrefs(
        'reporting_audit_inventory_cols',
        AUDIT_INVENTORY_COLUMN_KEYS,
        AUDIT_INVENTORY_PINNED_KEYS,
        AUDIT_INVENTORY_DEFAULT_HIDDEN,
        { orderedQuantity: 'orderedAndInitialQuantity', type: 'adjustmentType' },
    );

    const inventoryColumnDrawerRows = useMemo(
        () =>
            columnPrefs.columnConfigForDrawer.map((r) => ({
                ...r,
                label: AUDIT_INVENTORY_SETTINGS_LABELS[r.key] ?? r.key,
            })),
        [columnPrefs.columnConfigForDrawer],
    );

    const drawerColumns = useMemo(
        () => ({
            rows: inventoryColumnDrawerRows,
            onToggle: columnPrefs.toggleVisible,
            onReorderDrag: columnPrefs.reorderByKey,
            onResetColumns: columnPrefs.reset,
            getDefaultRows: () =>
                AUDIT_INVENTORY_COLUMN_KEYS.map((key) => ({
                    key,
                    label: AUDIT_INVENTORY_SETTINGS_LABELS[key] ?? key,
                    visible: !AUDIT_INVENTORY_DEFAULT_HIDDEN.includes(key),
                    pinned: AUDIT_INVENTORY_PINNED_KEYS.includes(key),
                })),
        }),
        [
            inventoryColumnDrawerRows,
            columnPrefs.toggleVisible,
            columnPrefs.reorderByKey,
            columnPrefs.reset,
        ],
    );

    const handleCommitColumns = useCallback(
        (rows: SettingsColumnDrawerRow[]) => {
            columnPrefs.setPrefs(
                columnDrawerRowsToPrefs(rows, AUDIT_INVENTORY_COLUMN_KEYS),
            );
        },
        [columnPrefs],
    );

    const drawerFiltersSnapshotRef = useRef<ReportingInventoryFilterSnapshot | null>(null);

    const handleSettingsOpen = useCallback(() => {
        drawerFiltersSnapshotRef.current = structuredClone(getSnapshot());
    }, [getSnapshot]);

    const handleSettingsSave = useCallback(() => {
        drawerFiltersSnapshotRef.current = null;
    }, []);

    const handleSettingsDiscard = useCallback(() => {
        const snap = drawerFiltersSnapshotRef.current;
        if (!snap) return;
        restoreSnapshot(snap);
        drawerFiltersSnapshotRef.current = null;
    }, [restoreSnapshot]);

    // Auditing chrome — MultiSelectTrigger / Checkbox menus (not FilterBar)
    const filterContent = (
        <VStack align="stretch" spacing={4}>
            {!locationId && (
                <Box>
                    <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={2}>
                        LOCATIONS
                    </Text>
                    <LocationSelector
                        selectedLocations={selectedLocations}
                        onLocationChange={(next) => {
                            setSelectedLocations(next);
                            setSelectedBinIds([]);
                            setBinLabelById({});
                        }}
                        width="100%"
                        icon={TbBuilding as ElementType}
                    />
                </Box>
            )}
            <Box>
                <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={2}>
                    PART TYPES
                </Text>
                <MultiSelectTrigger label="All Part Types" selectedCount={selectedPartTypes.length}>
                    <VStack align="stretch" spacing={1} maxH="220px" overflowY="auto">
                        {partTypeOptions.map((opt) => (
                            <Checkbox
                                key={opt.value}
                                size="sm"
                                isChecked={selectedPartTypes.includes(
                                    opt.value as (typeof selectedPartTypes)[number],
                                )}
                                onChange={() => {
                                    const value = opt.value as (typeof selectedPartTypes)[number];
                                    setSelectedPartTypes((prev) =>
                                        prev.includes(value)
                                            ? prev.filter((v) => v !== value)
                                            : [...prev, value],
                                    );
                                }}
                            >
                                <Text fontSize="sm">{opt.label}</Text>
                            </Checkbox>
                        ))}
                    </VStack>
                </MultiSelectTrigger>
            </Box>
            <Box>
                <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={2}>
                    TAGS
                </Text>
                <MultiSelectTrigger label="All Tags" selectedCount={selectedTagIds.length}>
                    <SearchableCheckboxList
                        search={tagSearch}
                        onSearchChange={setTagSearch}
                        searchPlaceholder="Search tags…"
                        isLoading={isTagsLoading}
                        options={tagOptions}
                        selectedIds={selectedTagIds}
                        onToggle={(value, label) =>
                            toggleId(setSelectedTagIds, setTagLabelById, value, label)
                        }
                    />
                </MultiSelectTrigger>
            </Box>
            <Box>
                <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={2}>
                    BINS
                </Text>
                <MultiSelectTrigger label="All Bins" selectedCount={selectedBinIds.length}>
                    <SearchableCheckboxList
                        search={binSearch}
                        onSearchChange={setBinSearch}
                        searchPlaceholder="Search bins…"
                        isLoading={isBinsLoading}
                        options={binOptions}
                        selectedIds={selectedBinIds}
                        onToggle={(value, label) =>
                            toggleId(setSelectedBinIds, setBinLabelById, value, label)
                        }
                    />
                </MultiSelectTrigger>
            </Box>
            <Box>
                <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={2}>
                    SUPPLIERS
                </Text>
                <MultiSelectTrigger label="All Suppliers" selectedCount={selectedSupplierIds.length}>
                    <SearchableCheckboxList
                        search={supplierSearch}
                        onSearchChange={setSupplierSearch}
                        searchPlaceholder="Search suppliers…"
                        isLoading={isSuppliersLoading}
                        options={supplierOptions}
                        selectedIds={selectedSupplierIds}
                        onToggle={(value, label) =>
                            toggleId(setSelectedSupplierIds, setSupplierLabelById, value, label)
                        }
                    />
                </MultiSelectTrigger>
            </Box>
        </VStack>
    );

    const handleExport = useCallback(
        (format: InventoryReportingExportFormat) => {
            const columns = columnPrefs.visibleOrderedKeys
                .filter((key): key is InventoryReportingExportColumnKey =>
                    AUDIT_INVENTORY_EXPORT_COLUMN_KEYS.has(key as InventoryReportingExportColumnKey),
                )
                .map((key) => ({
                    key,
                    label: AUDIT_INVENTORY_LABELS[key] ?? key,
                }));

            exportMutation.mutate({
                startDate,
                endDate,
                locationIds: locationIdsForApi,
                preset,
                tagIds: selectedTagIds.length ? selectedTagIds : undefined,
                binIds: selectedBinIds.length ? selectedBinIds : undefined,
                partTypes: selectedPartTypes.length ? selectedPartTypes : undefined,
                supplierIds: selectedSupplierIds.length ? selectedSupplierIds : undefined,
                search: apiFilters.search,
                format,
                columns,
            });
        },
        [
            apiFilters.search,
            columnPrefs.visibleOrderedKeys,
            endDate,
            exportMutation,
            locationIdsForApi,
            preset,
            selectedBinIds,
            selectedPartTypes,
            selectedSupplierIds,
            selectedTagIds,
            startDate,
        ],
    );

    const adjustments = data?.adjustments ?? [];
    const totalCount = data?.totalCount ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

    const table = (
        <AuditingInventoryTable
            rows={adjustments}
            visibleColumnKeys={columnPrefs.visibleOrderedKeys}
            isLoading={isLoading && !data}
        />
    );

    return {
        searchQuery,
        setSearchQuery,
        page,
        setPage,
        totalPages,
        isLoading: isLoading || isFetching,
        filterContent,
        drawerColumns,
        handleCommitColumns,
        badgeCount: activeFilterCount,
        table,
        handleExport,
        isExporting: exportMutation.isPending,
        handleSettingsOpen,
        handleSettingsSave,
        handleSettingsDiscard,
        apiFilters,
        filters,
    };
}
